import { appStore } from "src/appStore";
import { onError } from "src/common/onError";
import { ModelessFormStore } from "src/components/ModelessForm";
import { SelectDictionaryStore } from "../Obj2Tab/forms/AttrForm2/SelectDictionary";
import { ObjTableSettingsStore } from "../Obj2Tab/ObjTableSettings/ObjTableSettingsStore";
import {
  formDataContextOf,
  kindContextOf,
  saveContextOf,
} from "./flow/context";
import { DraftNodes } from "./flow/drafts";
import { NodeLoading } from "./flow/loading";
import { ObjectsMutations } from "./flow/mutations";
import { NodeSelection } from "./flow/selection";
import { BusinessServicesFlow } from "./flow/services";
import { Startup } from "./flow/startup";
import { nodeFormData } from "./kinds/registry";
import { getCurrentObjectCacheKey, getCurrentUserKey } from "./model/cacheKey";
import { CatalogStore } from "./model/catalog";
import { ObjectDetailsStore } from "./model/details";
import { ExpansionStore } from "./model/expansion";
import { FormDataStore } from "./model/formData";
import { NodeId } from "./model/node";
import { ObjectServicesStore } from "./model/objectServices";
import { ReferencesStore } from "./model/references";
import { SelectionStore } from "./model/selection";
import { TreeStore } from "./model/tree";
import { OrderEditorStore } from "./order/orderEditorStore";
import { ProjectionContext } from "./view/projectNode";
import { TreeProjection } from "./view/treeProjection";

/**
 * Сборка вкладки: кто из кого состоит.
 *
 * Отдельно от фасада, чтобы порядок создания модулей и их связи читались в
 * одном месте, а не тонули среди делегирующих методов.
 */
export type ObjectsModules = ReturnType<typeof createModules>;

export const createModules = () => {
  const expansion = new ExpansionStore();
  const tree = new TreeStore(expansion);
  const references = new ReferencesStore();
  const details = new ObjectDetailsStore(getCurrentObjectCacheKey);
  const services = new ObjectServicesStore(getCurrentObjectCacheKey);
  const catalog = new CatalogStore(tree, () => appStore.curLang);
  const selection = new SelectionStore();
  const kindCtx = kindContextOf(tree, details, services);

  // Форма умеет две вещи: сохранять текущий узел и снимать несохранённый
  // черновик, если пользователь от него отказался
  const formStore: ModelessFormStore = new ModelessFormStore(
    (values: unknown) => mutations.save(values),
    () => drafts.dropIfDraft().catch(onError),
  );

  const formData: FormDataStore = new FormDataStore(
    formStore,
    (token) => selection.isActual(token),
    (node) =>
      nodeFormData(
        node,
        formDataContextOf(tree, details, services, references),
      ),
  );
  const loading = new NodeLoading(tree, details, services, catalog, kindCtx);
  const nodeSelection: NodeSelection = new NodeSelection(
    selection,
    tree,
    formData,
    loading,
    formStore,
  );
  const mutations: ObjectsMutations = new ObjectsMutations(
    tree,
    catalog,
    details,
    services,
    selection,
    loading,
    nodeSelection,
    saveContextOf(tree, details, services, references),
  );
  const drafts: DraftNodes = new DraftNodes(
    tree,
    catalog,
    selection,
    expansion,
    nodeSelection,
    loading,
  );

  /**
   * Забыть всё, что относится к прежнему пользователю.
   *
   * Перечислено явно и в одном месте: новое хранилище легко забыть, а забытое
   * хранилище означает чужие данные на экране.
   */
  const reset = () => {
    selection.select(null);
    formData.clear();
    tree.clear();
    expansion.clear();
    catalog.clear();
    details.clear();
    services.clear();
  };

  const projection: ProjectionContext = {
    ...kindCtx,
    children: (id: NodeId) => tree.children(id),
    services: (objectId) => services.servicesOf(objectId),
    attributes: (objectId) => services.attributesOf(objectId),
  };

  return {
    treeProjection: new TreeProjection(tree, projection),
    expansion,
    tree,
    references,
    details,
    services,
    catalog,
    selection,
    formStore,
    formData,
    loading,
    nodeSelection,
    mutations,
    drafts,
    projection,
    business: new BusinessServicesFlow(services),
    startup: new Startup(
      references,
      catalog,
      selection,
      nodeSelection,
      loading,
      getCurrentUserKey,
      reset,
    ),
    orderStore: new OrderEditorStore((objectId) => details.ensure(objectId)),
    tableSettingsStore: new ObjTableSettingsStore(),
    selectDictionaryStore: new SelectDictionaryStore(),
  };
};
